/**
 * TODO: Have qualified legal counsel review and approve this Privacy Policy
 * before relying on it for production launch or regulatory compliance.
 * This is a product-ready starter policy, not legal advice.
 */
import { LEGAL_LAST_UPDATED, SUPPORT_EMAIL } from './constants';
import type { LegalSection } from './types';

export const PRIVACY_PAGE_TITLE = 'Privacy Policy';

export const PRIVACY_BETA_NOTICE =
  'Scoutlytics is currently in beta. This Privacy Policy is subject to revision as our legal review progresses.';

export const PRIVACY_INTRO = [
  'This Privacy Policy explains how Scoutlytics ("we," "us," or "our") collects, uses, shares, and protects information when you use Scoutlytics, League Stream Center, and related services (collectively, the "Service").',
  `Last updated: ${LEGAL_LAST_UPDATED}.`,
  'By using the Service, you agree to the practices described in this Privacy Policy.',
];

export const PRIVACY_SECTIONS: LegalSection[] = [
  {
    id: 'information-we-collect',
    title: '1. Information We Collect',
    paragraphs: [
      'We collect information you provide directly, information generated through your use of the Service, and information from connected third-party accounts when you authorize integrations.',
    ],
    bullets: [
      'Name and display name',
      'Email address, if available through a login provider or account settings',
      'Discord ID, username, avatar, and guild/server-related identifiers where authorized',
      'Twitch login, Twitch user ID, broadcaster metadata, and VOD metadata',
      'YouTube channel and public video metadata when you authorize youtube.readonly for archive durability',
      'League names, team names, team assignments, gamer tags, schedules, standings, and match data',
      'Uploaded or imported CSV, NeonSportz, or other league source data',
      'VOD candidate metadata, review status, archive status, and publication settings',
      'Frame verification evidence and derived labels such as detected teams, gamer tags, schedule match candidates, confidence indicators, and commissioner review outcomes',
      'Usage logs, device and browser information, and IP-derived security logs where applicable',
      'Payment or customer identifiers if and when subscriptions or paid features are enabled',
    ],
  },
  {
    id: 'account-login',
    title: '2. Account and Login Information',
    paragraphs: [
      'When you sign in, we may receive identifiers and profile information from authentication providers such as Twitch or Discord. We use this information to create and maintain your account, secure sessions, and provide access to league features you are authorized to use.',
    ],
  },
  {
    id: 'twitch-data',
    title: '3. Twitch Data',
    paragraphs: [
      'If you connect Twitch, we may process Twitch user IDs, logins, display names, broadcaster metadata, VOD titles, descriptions, timestamps, durations, thumbnails, URLs, viewability status, and related metadata needed to sync and organize league VOD candidates.',
      'We do not use Twitch data for unrelated advertising.',
    ],
  },
  {
    id: 'youtube-data',
    title: '4. YouTube Data',
    paragraphs: [
      'If you connect a Google account for YouTube archive durability, we request the youtube.readonly OAuth scope. Through the YouTube Data API we may access and store: your YouTube channel ID and channel title; and metadata for your public videos (video IDs, titles, descriptions, publish timestamps, durations, thumbnails, and related public fields). We do not request scopes that upload, edit, delete, or manage your YouTube content.',
      'We use this YouTube user data solely to operate League Stream Center: linking a member\'s public game VODs to scheduled league games and playing back commissioner-published archive records when Twitch VODs expire or become unavailable. We do not use YouTube data for advertising, credit decisions, or unrelated product personalization.',
      'Scoutlytics\'s use and transfer to any other app of information received from Google APIs will adhere to the Google API Services User Data Policy, including the Limited Use requirements. We do not sell YouTube/Google user data. We do not transfer YouTube/Google user data to third parties except (a) as necessary to provide or improve user-facing features that are prominent in League Stream Center (for example cloud hosting that stores encrypted tokens and archive metadata), (b) for security/fraud prevention, or (c) as required by law. We do not allow humans to read YouTube/Google user data unless we have your affirmative consent, it is necessary for security/compliance, or we aggregate and anonymize it.',
      'You can disconnect YouTube access where the Service supports doing so. Revoking access in your Google Account settings also stops further YouTube API reads.',
    ],
  },
  {
    id: 'discord-data',
    title: '5. Discord Data',
    paragraphs: [
      'If you connect Discord or use Discord-related features, we may process Discord user IDs, usernames, avatars, and authorized guild or server-related identifiers needed to support authentication, account linking, or product functionality you enable.',
      'We do not use Discord data for unrelated advertising.',
    ],
  },
  {
    id: 'league-data',
    title: '6. League, Member, and Profile Information',
    paragraphs: [
      'We process league membership roles, commissioner actions, imported schedules, side labels, identities, join requests, invites, claims, and other league workflow data needed to operate Stream Center.',
    ],
  },
  {
    id: 'vod-verification',
    title: '7. VOD Metadata and Frame Verification Evidence',
    paragraphs: [
      'Stream Center may store VOD candidate metadata and frame verification outputs, including sampled-frame analysis results, detected team or gamer-tag labels, evidence summaries, confidence indicators, and commissioner review decisions.',
      'We store verification evidence and derived labels to support review workflows. We do not use raw video storage as a general product feature for these verification flows.',
    ],
  },
  {
    id: 'payments',
    title: '8. Payment Information',
    paragraphs: [
      'If paid features are offered, payment details are typically processed by third-party payment processors. We may receive billing status, transaction identifiers, and limited customer information needed for account management, support, and compliance.',
    ],
  },
  {
    id: 'cookies',
    title: '9. Cookies and Session Data',
    paragraphs: [
      'We use cookies and similar technologies to maintain sessions, remember preferences such as theme settings, protect against fraud, and understand Service performance. You can control some cookie behavior through your browser settings, but disabling cookies may affect login and core functionality.',
    ],
  },
  {
    id: 'how-we-use',
    title: '10. How We Use Information',
    bullets: [
      'Authentication and account/session management',
      'League and VOD organization, matching, and archive workflows',
      'Commissioner review, publication, and member access controls',
      'Fraud prevention, security monitoring, and debugging',
      'Product improvement, analytics, and reliability work',
      'Customer support and service communications',
      'Legal, compliance, and enforcement obligations',
    ],
    paragraphs: [
      'We use information to:',
    ],
  },
  {
    id: 'how-we-share',
    title: '11. How We Share Information',
    paragraphs: [
      'We do not sell personal data. We may share information with service providers that help us operate the Service, such as hosting and cloud infrastructure providers, authentication providers, payment processors, analytics or logging providers, and third-party integrations you choose to enable.',
      'We may disclose information if required by law, to protect rights and safety, or in connection with a merger, acquisition, or asset sale subject to appropriate protections.',
      'League private data is not made public unless a commissioner or authorized user publishes it in the product or otherwise configures it for member or public visibility.',
    ],
  },
  {
    id: 'third-party-services',
    title: '12. Third-Party Services',
    paragraphs: [
      'Third-party platforms such as Twitch, YouTube / Google, Discord, NeonSportz, and cloud providers may process information under their own privacy policies. Scoutlytics is not responsible for third-party practices outside our Service.',
    ],
  },
  {
    id: 'retention',
    title: '13. Data Retention',
    paragraphs: [
      'We retain information for as long as needed to provide the Service, support league workflows, comply with legal obligations, resolve disputes, and enforce agreements. Retention periods may vary based on data type, league activity, and backup or audit requirements.',
    ],
  },
  {
    id: 'security',
    title: '14. Security',
    paragraphs: [
      'We use reasonable administrative, technical, and organizational measures designed to protect information. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.',
    ],
  },
  {
    id: 'choices',
    title: '15. Your Choices and Deletion Requests',
    paragraphs: [
      'You may disconnect integrations where the Service supports doing so, update certain account information, and contact us to request access, correction, or deletion of personal information subject to applicable law and operational requirements.',
      `Deletion or privacy requests may be sent to ${SUPPORT_EMAIL}. We may need to retain certain information where required for security, backup, billing, or legal compliance.`,
    ],
  },
  {
    id: 'children',
    title: '16. Children and Minors',
    paragraphs: [
      'The Service is not directed to children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child has provided personal information, contact us so we can review and take appropriate action.',
    ],
  },
  {
    id: 'international',
    title: '17. International Users',
    paragraphs: [
      'If you access the Service from outside the United States, you understand that information may be processed and stored in the United States or other countries where we or our service providers operate, which may have different data protection laws than your jurisdiction.',
    ],
  },
  {
    id: 'changes',
    title: '18. Changes to This Privacy Policy',
    paragraphs: [
      'We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated" date above and, where appropriate, provide additional notice.',
    ],
  },
  {
    id: 'contact',
    title: '19. Contact',
    paragraphs: [
      `Privacy questions or requests may be sent to ${SUPPORT_EMAIL}.`,
    ],
  },
];
